import { useState, useEffect } from "react";
import { Eye, EyeOff, Save, RefreshCw, BarChart3, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { API } from "../../config/api";

export default function AdminTicketInventory() {
  const [slots, setSlots] = useState([]);
  const [salesReport, setSalesReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [editedCapacities, setEditedCapacities] = useState({});
  const [editedPrices, setEditedPrices] = useState({});
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
    "Content-Type": "application/json"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [slotsRes, reportRes] = await Promise.all([
        fetch(`${API}/admin/ticket-slots/vishu-2026`, { headers: getAuthHeaders() }),
        fetch(`${API}/admin/ticket-sales-report?event_slug=vishu-2026`, { headers: getAuthHeaders() })
      ]);

      if (slotsRes.ok) {
        const slotsData = await slotsRes.json();
        setSlots(slotsData);
        // Initialize edited capacities and prices
        const caps = {};
        const prices = {};
        slotsData.forEach(slot => {
          slot.ticket_types?.forEach(tt => {
            caps[`${slot.slot_id}_${tt.code}`] = tt.capacity_total;
            prices[`${slot.slot_id}_${tt.code}`] = tt.price;
          });
        });
        setEditedCapacities(caps);
        setEditedPrices(prices);
      }

      if (reportRes.ok) {
        setSalesReport(await reportRes.json());
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load inventory data");
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = async (slotId, currentVisibility) => {
    setSaving(prev => ({ ...prev, [`vis_${slotId}`]: true }));
    try {
      const res = await fetch(`${API}/admin/ticket-slots/vishu-2026/${slotId}/visibility`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_visible: !currentVisibility })
      });

      if (res.ok) {
        toast.success(`Slot ${slotId} ${!currentVisibility ? "shown" : "hidden"}`);
        fetchData();
      } else {
        toast.error("Failed to update visibility");
      }
    } catch (error) {
      toast.error("Error updating visibility");
    } finally {
      setSaving(prev => ({ ...prev, [`vis_${slotId}`]: false }));
    }
  };

  const saveCapacity = async (slotId, ticketCode) => {
    const key = `${slotId}_${ticketCode}`;
    const newCapacity = editedCapacities[key];
    
    setSaving(prev => ({ ...prev, [`cap_${key}`]: true }));
    try {
      const res = await fetch(`${API}/admin/ticket-slots/vishu-2026/${slotId}/capacity`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          ticket_code: ticketCode, 
          capacity_total: newCapacity === "" || newCapacity === null ? null : parseInt(newCapacity)
        })
      });

      if (res.ok) {
        toast.success(`${ticketCode} capacity updated to ${newCapacity || "unlimited"}`);
        fetchData();
      } else {
        toast.error("Failed to update capacity");
      }
    } catch (error) {
      toast.error("Error updating capacity");
    } finally {
      setSaving(prev => ({ ...prev, [`cap_${key}`]: false }));
    }
  };

  const savePrice = async (slotId, ticketCode) => {
    const key = `${slotId}_${ticketCode}`;
    const newPrice = editedPrices[key];
    
    setSaving(prev => ({ ...prev, [`price_${key}`]: true }));
    try {
      const res = await fetch(`${API}/admin/ticket-slots/vishu-2026/${slotId}/price`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          ticket_code: ticketCode, 
          price: parseFloat(newPrice) || 0
        })
      });

      if (res.ok) {
        toast.success(`Ticket price updated to $${newPrice}`);
        fetchData();
      } else {
        toast.error("Failed to update price");
      }
    } catch (error) {
      toast.error("Error updating price");
    } finally {
      setSaving(prev => ({ ...prev, [`price_${key}`]: false }));
    }
  };

  const resetInventory = async (resetSold = false) => {
    if (resetSold && !window.confirm("This will reset sold counts to 0. Are you sure?")) {
      return;
    }

    setSaving(prev => ({ ...prev, reset: true }));
    try {
      const res = await fetch(`${API}/admin/ticket-slots/vishu-2026/reset-inventory`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ reset_sold: resetSold })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        toast.error("Failed to reset inventory");
      }
    } catch (error) {
      toast.error("Error resetting inventory");
    } finally {
      setSaving(prev => ({ ...prev, reset: false }));
    }
  };

  const previewCleanup = async () => {
    setSaving(prev => ({ ...prev, cleanup: true }));
    try {
      const res = await fetch(`${API}/admin/orders/cleanup-preview`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setCleanupPreview(data);
        setShowCleanupDialog(true);
      } else {
        toast.error("Failed to preview cleanup");
      }
    } catch (error) {
      toast.error("Error previewing cleanup");
    } finally {
      setSaving(prev => ({ ...prev, cleanup: false }));
    }
  };

  const executeCleanup = async () => {
    setCleaningUp(true);
    try {
      const res = await fetch(`${API}/admin/orders/cleanup-prelive`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ confirm: true })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowCleanupDialog(false);
        setCleanupPreview(null);
        fetchData(); // Refresh data
      } else {
        toast.error("Failed to execute cleanup");
      }
    } catch (error) {
      toast.error("Error executing cleanup");
    } finally {
      setCleaningUp(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-ticket-inventory">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h1 className="font-heading text-2xl text-kerala-green">Ticket Inventory</h1>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={() => resetInventory(false)}>
            Set Limits (171/75)
          </Button>
          <Button 
            variant="outline" 
            className="text-red-600 border-red-200 hover:bg-red-50"
            onClick={previewCleanup}
            disabled={saving.cleanup}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clean Pre-Live Orders
          </Button>
        </div>
      </div>

      {/* Cleanup Confirmation Dialog */}
      <Dialog open={showCleanupDialog} onOpenChange={setShowCleanupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Clean Pre-Live Orders
            </DialogTitle>
            <DialogDescription>
              This will archive test and pre-live orders. They will be hidden from admin view but not deleted.
            </DialogDescription>
          </DialogHeader>
          
          {cleanupPreview && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-semibold text-red-800">
                  {cleanupPreview.total_to_archive} orders will be archived
                </p>
                <p className="text-sm text-red-600 mt-1">
                  Cutoff date: {cleanupPreview.cutoff_date}
                </p>
              </div>
              
              <div className="text-sm">
                <p className="font-medium mb-2">Breakdown:</p>
                <ul className="space-y-1 text-gray-600">
                  <li>• Orders before cutoff: {cleanupPreview.breakdown.before_cutoff}</li>
                  <li>• Test Stripe sessions: {cleanupPreview.breakdown.with_test_session}</li>
                  {Object.entries(cleanupPreview.breakdown.by_status || {}).map(([status, count]) => (
                    <li key={status}>• Status "{status}": {count}</li>
                  ))}
                </ul>
              </div>

              {cleanupPreview.sample_orders?.length > 0 && (
                <div className="text-sm">
                  <p className="font-medium mb-2">Sample orders to archive:</p>
                  <div className="bg-gray-50 rounded p-2 space-y-1 text-xs font-mono">
                    {cleanupPreview.sample_orders.map((o, i) => (
                      <div key={i} className="truncate">
                        {o.id} - {o.customer_name} - ${o.amount}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCleanupDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={executeCleanup}
              disabled={cleaningUp || !cleanupPreview?.total_to_archive}
            >
              {cleaningUp ? "Archiving..." : `Archive ${cleanupPreview?.total_to_archive || 0} Orders`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary Cards */}
      {salesReport && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Orders</CardDescription>
              <CardTitle className="text-2xl">{salesReport.summary.total_orders}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Adults Sold</CardDescription>
              <CardTitle className="text-2xl text-blue-600">{salesReport.summary.adults_sold}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Kids Sold</CardDescription>
              <CardTitle className="text-2xl text-purple-600">{salesReport.summary.kids_sold}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Revenue</CardDescription>
              <CardTitle className="text-2xl text-green-600">${(salesReport.summary.total_revenue / 100).toFixed(2)}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Paid / Pending</CardDescription>
              <CardTitle className="text-2xl">{salesReport.summary.paid_orders} / {salesReport.summary.pending_orders}</CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* Slot Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Slot Configuration & Pricing
          </CardTitle>
          <CardDescription>Manage slot visibility, capacities, and ticket prices</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Slot</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Visibility</TableHead>
                  <TableHead>Adult (Price / Cap / Sold / Rem)</TableHead>
                  <TableHead>Kids (Price / Cap / Sold / Rem)</TableHead>
                  <TableHead>Infants</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slots.map(slot => {
                  const adult = slot.ticket_types?.find(t => t.code === "adult") || {};
                  const kids = slot.ticket_types?.find(t => t.code === "kids") || {};
                  const infants = slot.ticket_types?.find(t => t.code === "infants") || {};
                  
                  const adultRemaining = adult.capacity_total !== null ? Math.max(0, (adult.capacity_total || 0) - (adult.capacity_sold || 0)) : "∞";
                  const kidsRemaining = kids.capacity_total !== null ? Math.max(0, (kids.capacity_total || 0) - (kids.capacity_sold || 0)) : "∞";

                  return (
                    <TableRow key={slot.slot_id} className={!slot.is_visible ? "opacity-50" : ""}>
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-kerala-green/10 text-kerala-green">
                          {slot.slot_name}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {slot.start_time} - {slot.end_time}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant={slot.is_visible ? "default" : "outline"}
                          size="sm"
                          onClick={() => toggleVisibility(slot.slot_id, slot.is_visible)}
                          disabled={saving[`vis_${slot.slot_id}`]}
                        >
                          {slot.is_visible ? (
                            <>
                              <Eye className="w-4 h-4 mr-1" />
                              Visible
                            </>
                          ) : (
                            <>
                              <EyeOff className="w-4 h-4 mr-1" />
                              Hidden
                            </>
                          )}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-2">
                          {/* Price */}
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400 w-6">$</span>
                            <Input
                              type="number"
                              className="w-16 h-8 text-sm"
                              value={editedPrices[`${slot.slot_id}_adult`] ?? ""}
                              onChange={(e) => setEditedPrices(prev => ({
                                ...prev,
                                [`${slot.slot_id}_adult`]: e.target.value
                              }))}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2"
                              onClick={() => savePrice(slot.slot_id, "adult")}
                              disabled={saving[`price_${slot.slot_id}_adult`]}
                            >
                              <Save className="w-3 h-3" />
                            </Button>
                          </div>
                          {/* Capacity */}
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span className="w-6">Cap:</span>
                            <Input
                              type="number"
                              className="w-16 h-7 text-xs"
                              value={editedCapacities[`${slot.slot_id}_adult`] ?? ""}
                              onChange={(e) => setEditedCapacities(prev => ({
                                ...prev,
                                [`${slot.slot_id}_adult`]: e.target.value
                              }))}
                              placeholder="∞"
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => saveCapacity(slot.slot_id, "adult")}
                              disabled={saving[`cap_${slot.slot_id}_adult`]}
                            >
                              <Save className="w-3 h-3" />
                            </Button>
                          </div>
                          <div className="text-xs">
                            Sold: {adult.capacity_sold || 0} | 
                            Rem: <strong className={adultRemaining <= 10 ? "text-red-500" : "text-green-600"}>{adultRemaining}</strong>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-2">
                          {/* Price */}
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400 w-6">$</span>
                            <Input
                              type="number"
                              className="w-16 h-8 text-sm"
                              value={editedPrices[`${slot.slot_id}_kids`] ?? ""}
                              onChange={(e) => setEditedPrices(prev => ({
                                ...prev,
                                [`${slot.slot_id}_kids`]: e.target.value
                              }))}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2"
                              onClick={() => savePrice(slot.slot_id, "kids")}
                              disabled={saving[`price_${slot.slot_id}_kids`]}
                            >
                              <Save className="w-3 h-3" />
                            </Button>
                          </div>
                          {/* Capacity */}
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span className="w-6">Cap:</span>
                            <Input
                              type="number"
                              className="w-16 h-7 text-xs"
                              value={editedCapacities[`${slot.slot_id}_kids`] ?? ""}
                              onChange={(e) => setEditedCapacities(prev => ({
                                ...prev,
                                [`${slot.slot_id}_kids`]: e.target.value
                              }))}
                              placeholder="∞"
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => saveCapacity(slot.slot_id, "kids")}
                              disabled={saving[`cap_${slot.slot_id}_kids`]}
                            >
                              <Save className="w-3 h-3" />
                            </Button>
                          </div>
                          <div className="text-xs">
                            Sold: {kids.capacity_sold || 0} | 
                            Rem: <strong className={kidsRemaining <= 10 ? "text-red-500" : "text-green-600"}>{kidsRemaining}</strong>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        <div>
                          <div>Price: FREE</div>
                          <div>Unlimited</div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Sales Report */}
      {salesReport && salesReport.rows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Sales</CardTitle>
            <CardDescription>Latest ticket purchases</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Slot</TableHead>
                    <TableHead>Ticket</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salesReport.rows.slice(0, 20).map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-sm">
                        {new Date(row.purchase_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{row.customer_name}</p>
                          <p className="text-xs text-gray-500">{row.customer_email}</p>
                          {row.customer_phone && (
                            <p className="text-xs text-gray-400">{row.customer_phone}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-kerala-green/10 text-kerala-green">
                          {row.slot_name || row.slot_id}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                          row.ticket_type === "Adult" ? "bg-blue-100 text-blue-700" :
                          row.ticket_type === "Kids" ? "bg-purple-100 text-purple-700" :
                          "bg-green-100 text-green-700"
                        }`}>
                          {row.ticket_type}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">{row.quantity}</TableCell>
                      <TableCell>${((row.price_cents * row.quantity) / 100).toFixed(2)}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          row.order_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                        }`}>
                          {row.order_status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
